import { useEffect, useMemo, useState } from 'react';

import { storeKeys, useShowConsumerCheckout } from '@givebacks/api/store';
import { studentKeys } from '@givebacks/api/student';
import { arrayHasLength } from '@givebacks/utils/array';
import { useQueryClient } from '@tanstack/react-query';
import { useNativeDonationManagement, usePlatformService } from 'core';
import { useParams } from 'react-router-dom';
import { LoadingOverlay } from 'ui';

import { useShopStorage } from '../../hooks/useShopStorage';
import { useShopRoutes } from '../../routes/useShopRoutes';
import { CheckoutConfirmationModal } from './CheckoutConfirmationModal';

const NativeCheckoutModal = () => {
  const queryClient = useQueryClient();
  const platform = usePlatformService();
  const { checkoutId } = useParams();
  const { closeCheckoutRoute } = useShopRoutes();
  const { updateShopCart, removeCart } = useShopStorage();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { data: checkoutResponse, refetch: refetchCheckout } = useShowConsumerCheckout({
    checkoutId: checkoutId ?? '',
  });

  const cartIds = useMemo(() => {
    return checkoutResponse?.checkout.carts.map((cart) => cart.uuid) ?? [];
  }, [checkoutResponse]);

  // Handle failure on native checkout, this should
  // be called when the native checkout fails.
  // It will update the cart with the latest data
  const handleFailure = async () => {
    try {
      const response = await refetchCheckout();
      const { data: checkoutResponse } = response;
      if (!checkoutResponse) return;
      for (const cart of checkoutResponse.checkout.carts) {
        const vars = { cart, storeId: cart.cause_id, cartId: cart.uuid };
        updateShopCart(vars);
      }
    } catch {
      // Remove the cart if the api fails to fetch the cart
      if (arrayHasLength(cartIds)) {
        cartIds.forEach((cartId) => {
          removeCart(cartId);
        });
      }
      closeCheckoutRoute();
    }

    closeCheckoutRoute();
  };

  const { openNativeDonation } = useNativeDonationManagement({
    type: 'cause_donation',
    onSuccess() {
      setIsModalOpen(true);
      setIsLoading(false);
      // Remove the cart if the native checkout is successful
      if (arrayHasLength(cartIds)) {
        cartIds.forEach((cartId) => {
          removeCart(cartId);
        });
      }
    },
    onFailure() {
      void handleFailure();
    },
    onExit() {
      setIsLoading(false);
      // Wide invalidation do ensure any store data is updated
      void queryClient.invalidateQueries({ queryKey: storeKeys.all });
      void queryClient.invalidateQueries({ queryKey: studentKeys.all });
    },
  });

  useEffect(() => {
    if (platform.isNative) {
      const searchParams = new URLSearchParams();

      if (checkoutId) {
        searchParams.append('checkoutId', checkoutId);
        void openNativeDonation({ routeParams: `&${searchParams.toString()}` });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only cause and cartId changes should trigger an update.
  }, [checkoutId]);

  if (!checkoutId) return null;
  return (
    <>
      <LoadingOverlay visible={isLoading} />
      <CheckoutConfirmationModal
        opened={isModalOpen}
        onClose={closeCheckoutRoute}
        checkoutId={checkoutId}
      />
    </>
  );
};

export { NativeCheckoutModal };
